import { useState, useEffect } from 'react';
import { Shield, CheckSquare, Save, Info } from 'lucide-react';
import Modal from '../common/Modal';
import { useToast } from '../common/ToastContainer';

// RBAC permission constants
const ROLE_PERMISSIONS: Record<string, string[]> = {
  Admin: ['create', 'read', 'update', 'delete'],
  Editor: ['read', 'update'],
  Viewer: ['read'],
  Custom: ['read'], // User-defined (read always enabled)
};

const PERMISSION_LABELS: Record<string, string> = {
  create: 'Create',
  read: 'Read',
  update: 'Update',
  delete: 'Delete',
};

interface RoleFormData {
  id: string;
  roleName: string;
  roleType: 'Admin' | 'Editor' | 'Viewer' | 'Custom';
  description?: string;
  defaultRole?: boolean;
  permissions?: string[];
}

interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (role: RoleFormData) => void;
  role?: any; // Can accept API format
  mode: 'add' | 'edit';
}

const RoleModal = ({ isOpen, onClose, onSave, role, mode }: RoleModalProps) => {
  const toast = useToast();
  const [formData, setFormData] = useState<RoleFormData>({
    id: '',
    roleName: '',
    roleType: 'Viewer',
    description: '',
    defaultRole: false,
    permissions: [],
  });

  useEffect(() => {
    if (role) {
      // Map API format to form format
      setFormData({
        id: role.id || '',
        roleName: role.name || role.roleName || '',
        roleType: role.role_type || role.roleType || 'Custom',
        description: role.description || '',
        defaultRole: role.default_role || role.defaultRole || false,
        permissions: role.permissions || [],
      });
    } else {
      // Reset for add mode
      setFormData({
        id: '',
        roleName: '',
        roleType: 'Viewer',
        description: '',
        defaultRole: false,
        permissions: ['read'],
      });
    }
  }, [role, isOpen]);

  const handleChange = (field: keyof RoleFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRoleTypeChange = (roleType: 'Admin' | 'Editor' | 'Viewer' | 'Custom') => {
    const newPermissions = ROLE_PERMISSIONS[roleType];
    setFormData((prev) => ({
      ...prev,
      roleType,
      permissions: newPermissions,
    }));
  };

  const handlePermissionToggle = (permission: string) => {
    if (permission === 'read') return; // Read is always enabled
    setFormData((prev) => {
      const current = prev.permissions || [];
      const updated = current.includes(permission)
        ? current.filter((p) => p !== permission)
        : [...current, permission];
      return { ...prev, permissions: updated };
    });
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.roleName || formData.roleName.trim().length === 0) {
      toast.error('Validation Error', 'Role name is required');
      return;
    }

    // For Custom type, at least one permission must be selected
    if (formData.roleType === 'Custom' && (!formData.permissions || formData.permissions.length === 0)) {
      toast.error('Validation Error', 'Please select at least one permission for Custom role type');
      return;
    }

    onSave(formData);
    onClose();
  };

  const currentPermissions = formData.permissions || [];
  const isCustom = formData.roleType === 'Custom';

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button onClick={handleSubmit} className="btn-primary inline-flex items-center space-x-2">
        <Save className="w-4 h-4" />
        <span>{mode === 'add' ? 'Create Role' : 'Save Changes'}</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add New Role' : `Edit Role: ${role?.name || role?.roleName}`}
      size="2xl"
      footer={footer}
    >
      <div className="space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
            <Shield className="w-5 h-5 text-primary-600" />
            <span>Basic Information</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.roleName}
                onChange={(e) => handleChange('roleName', e.target.value)}
                placeholder="e.g., Support Team"
                className="input-base w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.roleType}
                onChange={(e) => handleRoleTypeChange(e.target.value as any)}
                className="input-base w-full"
                required
              >
                <option value="Admin">Admin (Full Access - CRUD)</option>
                <option value="Editor">Editor (Read & Update)</option>
                <option value="Viewer">Viewer (Read Only)</option>
                <option value="Custom">Custom (Choose Permissions)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Roles only apply to Tenant Admins (type=2)
              </p>
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
            <CheckSquare className="w-5 h-5 text-primary-600" />
            <span>Permissions</span>
          </h3>

          {/* Permission Summary */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-2">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  This role has the following permissions:
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {currentPermissions.length > 0 ? (
                    currentPermissions.map((perm) => (
                      <span
                        key={perm}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                      >
                        {PERMISSION_LABELS[perm] || perm}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-gray-500">No permissions assigned</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Permission Checkboxes (only for Custom type) */}
          {isCustom ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 mb-3">Select the permissions for this custom role:</p>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(PERMISSION_LABELS).map(([perm, label]) => (
                  <label key={perm} className={`flex items-center space-x-2 ${perm === 'read' ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={currentPermissions.includes(perm)}
                      onChange={() => handlePermissionToggle(perm)}
                      disabled={perm === 'read'}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      {label}{perm === 'read' && <span className="text-xs text-gray-500 ml-1">(always enabled)</span>}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Permissions are automatically assigned for <strong>{formData.roleType}</strong> role type.
                Choose "Custom" role type to manually select permissions.
              </p>
            </div>
          )}
        </div>

        {/* Settings */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Settings</h3>
          <div className="space-y-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.defaultRole}
                onChange={(e) => handleChange('defaultRole', e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Set as default role for new users</span>
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Optional description of this role's purpose..."
                className="input-base w-full"
                rows={3}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RoleModal;
